import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  styled,
  Typography,
  useTheme
} from '@mui/material';
import Button from 'components/Common/Button';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import Input from 'components/Common/Input';
import Grid from '@mui/material/Grid2';
import Select, { Option } from 'components/Common/Select';
import { useGetAgent, useUpdateAgent } from 'apis/business/agent/hook';
import { useGetBusinessVoices } from 'apis/business/agent/voice/hook';
import Switch from 'components/Common/Switch';
import CloneVoiceModal from 'components/Common/CloneVoice';
import Spinner from 'components/Common/Spinner';
import { IoChevronDownOutline } from 'react-icons/io5';
import { titleCase } from 'utils/helperFunctions';
import { useGetAgentLanguages } from 'apis/business/agent/languages/hook';

export default function AgentConfig() {
  const theme = useTheme();
  const [voiceType, setVoiceType] = useState('voiceCollection');
  const [voiceOptions, setVoiceOptions] = useState<Option[]>([]);
  // const [sttOptions, setSttOptions] = useState<Option[]>([]);
  // const [ttsOptions, setTtsOptions] = useState<Option[]>([]);

  const [currentPage, setCurrentPage] = useState(1);

  const { data: agent, isLoading: isAgentLoading } = useGetAgent();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(agent?.avatar_url ? agent.avatar_url : null);

  const { control, watch } = useFormContext();
  const isWelcomeMessageEnabled = watch('agent.enable_welcome_message');

  const { data: voices, isLoading: isLoadingVoicesLoading } = useGetBusinessVoices();
  const { data: languages, isLoading: isLanguagesLoading } = useGetAgentLanguages();
  // const { data: sttServices, isLoading: isLoadingSttServices } = useGetSttServices();
  // const { data: ttsServices, isLoading: isLoadingTtsServices } = useGetTtsServices();

  const { mutateAsync: updateAgent } = useUpdateAgent();

  useEffect(() => {
    if (voices?.data) {
      setVoiceOptions(() => [
        ...voices.data.map((item) => ({
          label: item.friendly_name,
          value: item.voice_id,
          audioUrl: item.audio_url
        }))
      ]);
    }
  }, [voices]);

  // useEffect(() => {
  //   if (sttServices?.data) {
  //     setSttOptions((prevOptions) => [
  //       ...prevOptions,
  //       ...sttServices.data.map((item) => ({
  //         label: item,
  //         value: item
  //       }))
  //     ]);
  //   }
  // }, [sttServices]);

  // useEffect(() => {
  //   if (ttsServices?.data) {
  //     setTtsOptions((prevOptions) => [
  //       ...prevOptions,
  //       ...ttsServices.data.map((item) => ({
  //         label: item,
  //         value: item
  //       }))
  //     ]);
  //   }
  // }, [ttsServices]);

  const loadMoreVoices = () => {
    if (!isLoadingVoicesLoading && currentPage < (voices?.meta.total_pages || 1)) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const newUrl = URL.createObjectURL(file);
      setAvatarUrl(newUrl);
      const formData = new FormData();
      formData.append('agent[avatar]', file);
      await updateAgent(formData);
    }
  };
  const isLoading = isLoadingVoicesLoading || isAgentLoading;

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Box
      bgcolor={theme.palette.background.paper}
      sx={{ border: '1px solid #F3F3F3' }}
      padding='24px'
      borderRadius='4px'
    >
      <Typography variant='h6'>Agent avatar</Typography>
      <Typography marginTop='6px' color='secondary'>
        Upload an Avatar for Your Agent
      </Typography>

      <Box
        sx={{
          gap: '16px',
          alignItems: 'center',
          marginTop: '8px',
          border: '1px solid #F3F3F3',
          borderRadius: '4px',
          padding: '16px'
        }}
        display={{ xs: 'block', sm: 'flex' }}
      >
        <Avatar
          src={avatarUrl || '/path/to/default/avatar.png'}
          alt={agent?.name ?? 'User Avatar'}
          sx={{ width: 64, height: 64 }}
        />
        <Box>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Button component='label' role={undefined} variant='outlined' tabIndex={-1}>
              Choose files
              <VisuallyHiddenInput type='file' onChange={handleFileChange} multiple />
            </Button>
          </Box>
          <Typography color='secondary' fontSize='12px'>
            The proposed size is 350px*180px. No bigger than 2.5MB{' '}
          </Typography>
        </Box>
      </Box>

      <Grid container spacing='24px' marginTop='26px'>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Input name='agent.name' label='Name' control={control} fullWidth required />
          <Typography color='secondary' fontSize={12}>
            What name will your assistant go by
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Select
            label='Language'
            disabled={isLanguagesLoading}
            name='agent.languages'
            control={control}
            options={
              languages?.data.map((item) => ({
                label: titleCase(item),
                value: item
              })) || []
            }
            multiple
            required
          />
          <Typography color='secondary' lineHeight={1.5} fontSize={12}>
            The language that your agent understands should be the same language that it speaks
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing='24px' marginTop='26px'>
        {/* <Grid size={{ xs: 12, sm: 6 }}>
          <Select
            label='STT'
            name='agent.stt_service'
            isLoading={isLoadingSttServices}
            control={control}
            options={sttOptions}
            onScrollEnd={loadMoreVoices}
            required
          />
        </Grid> */}
        {/* <Grid size={{ xs: 12, sm: 6 }}>
          <Select
            label='TTS'
            name='agent.tts_service'
            control={control}
            isLoading={isLoadingTtsServices}
            options={ttsOptions}
            onChange={(e) => {
              setValue('agent.tts_service', e?.target.value);
              setValue('agent.voice_id', '');
            }}
            required
          />
        </Grid> */}
      </Grid>
      <Typography fontWeight='500' marginTop='24px' marginBottom='8px'>
        Select Voice Type
      </Typography>
      {/* TODO - @need to work later */}
      {voices?.meta?.clone_voice && (
        <FormControl sx={{ marginBottom: '8px' }}>
          <RadioGroup
            row
            value={voiceType}
            onChange={(event) => setVoiceType(event.target.value)}
            aria-labelledby='demo-row-radio-buttons-group-label'
            name='row-radio-buttons-group'
          >
            <FormControlLabel value='voiceCollection' control={<Radio />} label='Voice Collections' />
            <FormControlLabel value='cloneVoice' control={<Radio />} label='Clone your voice' />
          </RadioGroup>
        </FormControl>
      )}
      {voiceType === 'voiceCollection' ? (
        <Box mt={1.5}>
          <Select
            label='Voice'
            name='agent.voice_id'
            isLoading={isLoadingVoicesLoading}
            control={control}
            options={voiceOptions}
            onScrollEnd={loadMoreVoices}
            required
          />
        </Box>
      ) : (
        <Box marginTop='4px'>
          <CloneVoiceModal />
        </Box>
      )}

      <Box my={3} p={2} borderRadius={0.5} border='1px solid #F3F3F3'>
        <Box display={{ xs: 'block', sm: 'flex' }} justifyContent='space-between' alignItems='center' gap='74px'>
          <Box>
            <Typography variant='h6'>Welcome Message</Typography>
            <Typography color='secondary' mt={2}>
              By default, our voice assistant will automatically greet callers and start the conversation with a welcome
              message, ensuring a smooth and professional experience.
            </Typography>
          </Box>
          <Switch name='agent.enable_welcome_message' control={control} />
        </Box>
        {isWelcomeMessageEnabled && (
          <Input
            name='agent.welcome_message'
            label=''
            control={control}
            placeholder='Enter your text here.....'
            sx={{ marginTop: '16px' }}
            minRows={4}
            multiline
            fullWidth
          />
        )}
      </Box>
      <Box
        sx={{
          marginY: '24px',
          border: '1px solid #F3F3F3',

          padding: '16px',
          borderRadius: '4px'
        }}
      >
        <Box
          display={{ xs: 'block', sm: 'flex' }}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '74px'
          }}
        >
          <Box>
            <Typography variant='h6'>Call recordings</Typography>
            <Typography color='secondary' marginTop='16px'>
              Toggle to record calls for playback and easy review in the logs.
            </Typography>
          </Box>
          <Box>
            <Switch name='agent.enable_call_recording' control={control} />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          marginTop: '24px',
          border: '1px solid #F3F3F3',
          padding: '16px',
          borderRadius: '4px'
        }}
      >
        <Box
          display={{ xs: 'block', sm: 'flex' }}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '74px'
          }}
        >
          <Box>
            <Typography variant='h6'>Transcribe filler words</Typography>
            <Typography color='secondary' marginTop='16px'>
              Transcribe words like "hmm" or "uhm”
            </Typography>
          </Box>
          <Box>
            <Switch name='agent.transcribe_filler_words' control={control} />
          </Box>
        </Box>
      </Box>
      <Accordion
        sx={{
          boxShadow: 'none',
          border: '1px solid #F3F3F3',
          marginTop: 2,
          '::before': { background: 'transparent' }
        }}
      >
        <AccordionSummary
          expandIcon={<IoChevronDownOutline style={{ fontSize: 22 }} />}
          aria-controls='panel1-content'
          id='panel1-header'
        >
          <Box display='flex' flexDirection='column'>
            <Typography variant='h6' marginBottom='16px'>
              Advanced Settings
            </Typography>
            <Typography color='secondary'>Customize every interaction to suit your support needs.</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing='16px'>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Select
                name='agent.answer_creativity'
                label='Answer creativity'
                control={control}
                options={Array.from({ length: 10 }, (_, i) => ({
                  label: (i + 1).toString(),
                  value: i + 1
                }))}
              />
              <Typography color='secondary' fontSize={12}>
                Select from 1 to 10 how creative the answers should be
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Select
                label='Speech stop sensitivity'
                name='agent.speech_stop_sensitivity'
                control={control}
                options={Array.from({ length: 10 }, (_, i) => ({
                  label: (i + 1).toString(),
                  value: i + 1
                }))}
              />
              <Typography color='secondary' fontSize={12}>
                Select how many words should stop the agent speech
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Input
                name='agent.concurrent_answer_generation'
                label='Concurrent answer gen..'
                control={control}
                fullWidth
                required
              />
              <Typography color='secondary' fontSize={12} sx={{ fontSize: '12px' }}>
                Start generating answer if the pause between words is (min 10 ms)
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Input name='agent.answer_length' label='Answer length' control={control} fullWidth required />
              <Typography color='secondary' fontSize={12}>
                Limit the maximum no. of words (approximately) per answer
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Input
                name='agent.silence_message_delay'
                label='Silence message delay'
                control={control}
                fullWidth
                required
              />
              <Typography color='secondary' fontSize={12}>
                Trigger the "are you there?" message after x seconds
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Input name='agent.silence_end_call' label='Silence end call' control={control} fullWidth required />
              <Typography color='secondary' fontSize={12}>
                End the call if there is no sound for xx seconds
              </Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
});
